import { useState, useMemo, useEffect, useRef } from 'react';

import { Button } from '@/components/core/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from '@/components/core/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/core/popover';
import { useAutoCompleteQuery } from '@/shared/hooks/queries/useAutoCompleteQuery';

import {
  Filter,
  FilterDefs,
  FilterDef,
  filterValueSchemas,
  SelectOption,
  UnsafeFilter,
} from './types';

const NOOP_QUERY = () => Promise.resolve({ items: [] });

type Props = {
  filter: Filter<FilterDefs, string>;
  filterDef: FilterDef<'select' | 'multiselect'>;
  onChange: (filter: UnsafeFilter) => void;
};

export function DataTableSelectInput({ filter, filterDef, onChange }: Props) {
  const isFirstRender = useRef(true); // used for autofocus

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const value = useMemo(() => {
    const parsedValue =
      filterDef.variant === 'select'
        ? filterValueSchemas.select.safeParse(filter.value)
        : filterValueSchemas.multiselect.safeParse(filter.value);

    if (!parsedValue.success || !parsedValue.data) {
      return [];
    }

    return parsedValue.data;
  }, [filter.value, filterDef.variant]);

  useEffect(() => {
    if (isFirstRender.current && value.length === 0) {
      setIsOpen(true);
    }
    isFirstRender.current = false;
  }, [value.length]);

  const { options: optionsProp = [] } = filterDef;
  const isStaticOptions = typeof optionsProp !== 'function';

  const {
    options: resolvedOptions,
    onSearch,
    isLoading,
  } = useAutoCompleteQuery({
    parentKey: `filter-${filter.id}`,
    query: isStaticOptions
      ? // useAutoCompleteQuery always requires a query fn
        // so as a workaround, we noop it when dealing with static options
        NOOP_QUERY
      : optionsProp,
    enabled: !isStaticOptions && isOpen && !!searchTerm,
  });

  const options = useMemo(() => {
    if (isStaticOptions) {
      // static options: let CMDK handle everything
      return optionsProp;
    }

    // dynamic options: show only selected options
    if (!searchTerm) {
      return value;
    }

    // dynamic options: show server-fetched options
    return resolvedOptions;
  }, [isStaticOptions, optionsProp, resolvedOptions, value, searchTerm]);

  const renderPlaceholder = () =>
    filterDef.variant === 'multiselect' ? 'Select options...' : 'Select option...';

  const renderSelectedValues = () => {
    if (value.length > 1) return `${value.length} selected`;
    if (filterDef.optionLabel) return filterDef.optionLabel(value[0]);
    return value[0];
  };

  const getSearchKeyword = (option: SelectOption) => {
    if (filterDef.optionLabel) {
      const label = filterDef.optionLabel(option);
      if (typeof label === 'string' || typeof label === 'number') {
        return String(label);
      }
    }
    return option;
  };

  const onSelectOption = (option: SelectOption) => {
    // Note: dropdown remains open after selection to avoid reordering flash
    let nextValue: SelectOption[];
    if (filterDef.variant === 'multiselect') {
      if (value.includes(option)) {
        // de-select the option
        nextValue = value.filter((v) => v !== option);
      } else {
        nextValue = [...value, option];
      }
    } else {
      nextValue = [option];
    }
    onChange({ id: filter.id, operator: filter.operator, value: nextValue });
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setSearchTerm('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-full min-w-16 rounded-none border border-input bg-control px-1.5 text-sm font-normal"
        >
          {value.length === 0 ? (
            renderPlaceholder()
          ) : (
            <span className="truncate">{renderSelectedValues()}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-w-72 min-w-48 p-0">
        <Command
          // Enable built-in filtering for static options, disable for dynamic
          // Static: CMDK handles all filtering and search behavior
          // Dynamic: assume options are already filtered server-side
          shouldFilter={isStaticOptions}
          // ignore value in search, instead use keywords (generated from option label)
          filter={(_, search, keywords) =>
            keywords?.some((keyword) =>
              keyword.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
            )
              ? 1
              : 0
          }
        >
          <CommandInput
            placeholder="Search options..."
            onValueChange={
              isStaticOptions
                ? // let CMDK handle everything for static options
                  undefined
                : // trigger options matching the input to be loaded from the server
                  (term) => {
                    setSearchTerm(term);
                    onSearch(term);
                  }
            }
          />
          <CommandList>
            {isLoading && <CommandLoading>Loading options...</CommandLoading>}
            {!isLoading && <CommandEmpty>No options found.</CommandEmpty>}
            <CommandGroup>
              {!isLoading &&
                options.map((option) => (
                  <CommandItem
                    key={option}
                    value={option}
                    data-checked={value.includes(option) ? 'true' : undefined}
                    keywords={[getSearchKeyword(option)]}
                    onSelect={() => onSelectOption(option)}
                  >
                    <span className="truncate">
                      {filterDef.optionLabel ? filterDef.optionLabel(option) : option}
                    </span>
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
